import { PermissionResponse } from '@business/entities/permission';
import { TrayItemType } from '@modules/dashboard/DashboardPage/DashboardToolbar';
import { BaseResponse } from '../common';
import { CompanyResponse } from '../companies/CompanyResponse';
import { TeamId } from '../team';
import { CustomFieldResponseType } from '../integration';
import { AccountSettingsResponse } from '../settings';

export enum UserStatus {
  active = 0,
  inactive = 1,
  dontknow = 2,
  dontknow2 = 3,
}

export enum UserRole {
  admin = 1,
  client = 2,
  user = 3,
  affiliate = 4,
  noLogin = 5,
}

export type UserId = string;
export type UserSocialId = string;

export interface UserSettingsResponse {
  trayItems?: TrayItemType[];
}

export interface AccountResponse {
  title: string;
  appId: string;
  logo?: string;
}

export enum UserType {
  fullTime = 'fullTime',
  partTime = 'partTime',
  contractor = 'contractor',
  asNeeded = 'asNeeded',
}
export interface UserResponse extends BaseResponse {
  id: UserId;

  token?: string;

  firstName: string;
  lastName: string;
  email: string;
  salary?: number;
  jobTitle?: string;
  personalEmail?: string;
  phone: string;
  resellerId: number;
  activation: string;
  status: UserStatus;
  /**
   * Whether the user may access the platform. Deactivated users keep their record and history.
   * Optional because only the full UserResource serializes it — the compact/tenant resources
   * omit it, so check `=== false` rather than falsiness.
   */
  isActive?: boolean;
  avatar: string | null | File;
  signature: string;
  companies: CompanyResponse[];
  socialAccounts: UserSocialAccountResponse[];
  settings: UserSettingsResponse;
  role: UserRole;
  password?: string;
  signatures: ChannelSignatureResponse[];
  accounts: AccountResponse[];
  permissions: PermissionResponse[];
  teams: TeamId[];
  teamsNames: string[];
  deletedAt: number | null;
  emails?: string[];
  customFields?: { [key: string]: CustomFieldResponseType };

  externalAccountId: string;
  externalAccountUrl: string;
  externalProductId: string;

  accountSettings: AccountSettingsResponse;
  makhAccounts: AccountResponse[];

  twoFactor: boolean;
  twoFactorEmail: boolean;
  twoFactorSMS: boolean;
  twoFactorAPP: boolean;

  slug: string;

  clockinEnabled?: boolean;
  manualTimeEnabled?: boolean;
  maximumHours?: string;
  allowOvertime?: boolean;
  ovetimeHours?: string;

  timezone?: string;
  openings?: UserOpeningResponse[];

  calendarSettings?: {
    duration: number;
    futureLimit: number;
    minBookingNotice: number;
    bufferTime: number;
    title: string;
    reminders: any[];
    fields: any[];
  };
  permissionAll?: boolean;

  startDate: number;
  endDate: number;
  userType: UserType;

  expectedCloseEmail: boolean;
  overLimitDailyEmail: boolean;
  weeklyLimitReachingEmail: boolean;
  startOnboarding?: boolean;
  notifications: { [key in string]: boolean };
  emailNotifications: { [key in string]: boolean };
}

export interface UserOpeningResponse {
  days: string[];
  start: string;
  end: string;
}

interface CompactCustomerResponse {
  id: string;
  name: string;
}

export interface TenantUserResponse extends UserResponse {
  customer: CompactCustomerResponse;
}

export type CompactUserResponse = Pick<
  UserResponse,
  'id' | 'firstName' | 'lastName' | 'email' | 'salary' | 'jobTitle' | 'role' | 'deletedAt' | 'avatar'
>;
export interface VeryCompactUserResponse {
  id: UserId;
  name: string;
  avatar?: string | null | File;
}

export interface UserSocialAccountResponse {
  id: UserSocialId;
  userId: UserId;
  email: string;
  name: string;
  signature: string;
  provider: SocialAccountProviders;
  accountId: string;
  status: SocialAccountStatus;
  schedulingLink: string;
  primaryCalendar: string;
}

export enum SocialAccountProviders {
  gmail = 'gmail',
  eas = 'eas',
  outlook = 'outlook',
  yahoo = 'yahoo',
  icloud = 'icloud',
  custom = 'custom',
}

export enum SocialAccountStatus {
  active = 'active',
  stopped = 'stopped',
  invalid = 'invalid',
}

export interface ChannelSignatureResponse {
  channelId: string;
  signature: string;
}

export interface SignupRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface LoginActivityResponse extends BaseResponse {
  id: string;
  ip: string;
}

export interface EmailItemResponse {
  email: string;
  name: string;
}

export interface TwoFactorResponse {
  token: string;
  status: boolean;
  code: string;
  type: TwoFactorType;
}

export interface TwoFactorEnableResponse {
  status: boolean;
  email: string;
  truncatedEmail: string;
  phone: string;
  availableTypes: TwoFactorType[];
}

export enum TwoFactorType {
  SMS = 'sms',
  EMAIL = 'email',
  PHONE = 'phone',
  APP = 'app',
}
